import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Brain, ArrowLeft, Sparkles, Loader2 } from "lucide-react";

export default function Login() {
  const [step, setStep] = useState(1); // 1: credentials, 2: OTP
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [devMode, setDevMode] = useState(false);
  const [devOtp, setDevOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [resentLink, setResentLink] = useState("");
  const { setUser } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  const onCred = async (e) => {
    e.preventDefault();
    setError(""); setNeedsConfirm(false); setResentLink(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setTempToken(data.temp_token);
      setMaskedEmail(data.email_masked);
      setDevMode(!!data.dev_mode);
      if (data.dev_mode) {
        try {
          const r = await api.get(`/auth/_dev_last_otp`, { params: { email } });
          setDevOtp(r.data.code);
        } catch {}
      }
      setStep(2);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Login failed";
      setError(msg);
      if (err.response?.status === 403 && /confirm/i.test(msg)) {
        setNeedsConfirm(true);
      }
    } finally { setBusy(false); }
  };

  const resendConfirmation = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/resend-confirmation", { email });
      toast.success("Confirmation link sent. Check your inbox.");
      if (data.confirm_link_preview) setResentLink(data.confirm_link_preview);
    } catch {
      toast.error("Could not resend right now");
    } finally { setBusy(false); }
  };

  const onOtp = async (e) => {
    e?.preventDefault();
    if (otp.length !== 6) { setError("Enter the 6-digit code"); return; }
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { temp_token: tempToken, code: otp });
      setUser(data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      const to = location.state?.from || "/dashboard";
      nav(to);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left visual */}
      <div className="hidden lg:flex flex-1 auth-bg text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-cyan-500 rounded-sm flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-slate-950" strokeWidth={2.5} />
            </div>
            <div className="font-display font-bold text-lg">CogniTrust<span className="text-cyan-400">.</span>AI</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-cyan-400 mb-3">Enterprise QA · AI Testing Marketplace</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight max-w-md">
              Secure 2FA login.<br/>Role-based access for testers, managers and admins.
            </h2>
            <p className="text-slate-400 mt-4 max-w-md leading-relaxed text-sm">A six-digit verification code will be emailed to you on every sign in. Codes expire in 10 minutes.</p>
          </div>
          <div className="text-xs text-slate-500 font-mono">cognitrust.ai/auth · v0.1.0</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-8" data-testid="link-back-home">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to home
          </Link>

          {step === 1 && (
            <>
              <div className="text-[11px] uppercase tracking-wider text-cyan-700 mb-2">Step 1 of 2</div>
              <h1 className="font-display text-3xl font-bold text-slate-950">Sign in</h1>
              <p className="text-slate-600 mt-2 text-sm">Enter your work email and password to receive a verification code.</p>
              <form onSubmit={onCred} className="mt-8 space-y-4" data-testid="form-login">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="mt-1.5" placeholder="you@company.com" autoCapitalize="none" autoCorrect="off" autoComplete="email" inputMode="email" enterKeyHint="next" data-testid="input-email" />
                </div>
                <div>
                  <Label htmlFor="pw">Password</Label>
                  <Input id="pw" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} className="mt-1.5" placeholder="••••••••" autoCapitalize="none" autoCorrect="off" autoComplete="current-password" enterKeyHint="go" data-testid="input-password" />
                </div>
                {error && <div className="text-sm text-red-600" data-testid="error-message">{error}</div>}
                {needsConfirm && (
                  <div className="text-sm border border-amber-200 bg-amber-50 text-amber-900 rounded-md p-3" data-testid="resend-confirm-block">
                    <div className="flex items-center justify-between gap-2">
                      <span>Didn't get the email?</span>
                      <Button type="button" variant="outline" size="sm" onClick={resendConfirmation} disabled={busy} data-testid="btn-resend-confirmation">
                        Resend confirmation link
                      </Button>
                    </div>
                    {resentLink && (
                      <a href={resentLink} className="block mt-2 text-xs text-cyan-700 underline break-all font-mono" data-testid="resent-confirm-link">{resentLink}</a>
                    )}
                  </div>
                )}
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={busy} data-testid="btn-login-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Continue"}
                </Button>
              </form>
              <div className="mt-8 text-center text-sm text-slate-600">
                New tester? <Link to="/register" className="text-cyan-700 font-medium hover:underline" data-testid="link-register">Apply to join</Link>
              </div>
              <div className="mt-10 text-[11px] text-slate-400 uppercase tracking-wider">Demo accounts</div>
              <div className="mt-2 text-xs text-slate-500 font-mono space-y-0.5">
                <div>admin@cognitrust.ai · Admin@123</div>
                <div>manager1@cognitrust.ai · Manager@123</div>
                <div>tester1@cognitrust.ai · Tester@123</div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="text-[11px] uppercase tracking-wider text-cyan-700 mb-2">Step 2 of 2 · Verification</div>
              <h1 className="font-display text-3xl font-bold text-slate-950">Enter code</h1>
              <p className="text-slate-600 mt-2 text-sm">We sent a 6-digit code to <span className="font-medium">{maskedEmail}</span>. The code expires in 10 minutes.</p>
              {devMode && (
                <div className="mt-4 p-3 border border-cyan-200 bg-cyan-50 rounded-md text-xs text-cyan-900" data-testid="dev-otp-hint">
                  <div className="font-semibold mb-1">Development mode</div>
                  Resend isn't configured. {devOtp ? <>Use code: <span className="font-mono font-bold text-base">{devOtp}</span></> : "Check backend logs for the OTP."}
                </div>
              )}
              <form onSubmit={onOtp} className="mt-6 space-y-5" data-testid="form-otp">
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="input-otp">
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} />)}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {error && <div className="text-sm text-red-600 text-center" data-testid="error-message">{error}</div>}
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={busy} data-testid="btn-verify-otp">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Verify & sign in"}
                </Button>
                <Button type="button" variant="ghost" className="w-full text-slate-600" onClick={() => { setStep(1); setOtp(""); setError(""); }} data-testid="btn-otp-back">
                  Use a different account
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
